/**
 * /pricing (`/de/preise`, `/fr/tarifs`, `/it/prezzi`). Composes the shared
 * pricing block (also used on the homepage) with the plan comparison table,
 * the billing FAQ and Product/Offer + FAQPage structured data. Every number
 * comes from `src/lib/pricing.ts`, every word from the `pricing.*` keys.
 */
import { interpolate, useTranslation } from '../lib/i18n'
import { pricingCopyValues } from '../lib/pricing'
import { buildFaqJsonLd, buildPricingJsonLd } from '../lib/seo'
import { FaqAccordion } from './faq-accordion'
import { PageIntro } from './not-found'
import { PlanComparison } from './pricing/plan-comparison'
import { PricingPlans } from './pricing/pricing-plans'

export function PricingPage() {
  const { locale, t, dict } = useTranslation()
  const copyValues = pricingCopyValues(locale)
  const faqItems = dict.pricing.faq.items.map((item) => ({
    question: interpolate(item.question, copyValues),
    answer: interpolate(item.answer, copyValues),
  }))

  return (
    <main className="page-shell pricing-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            buildPricingJsonLd({
              locale,
              descriptions: {
                pro: dict.pricing.plans.pro.tagline,
                team: dict.pricing.plans.team.tagline,
              },
            }),
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildFaqJsonLd(faqItems, locale)),
        }}
      />
      <PageIntro
        eyebrow={t('pricing.eyebrow')}
        title={t('pricing.title')}
        text={t('pricing.text')}
      />

      <PricingPlans surface="pricing_page" planHeading="h2" />

      <PlanComparison />

      <section className="pricing-faq" aria-labelledby="pricing-faq-title">
        <div className="section-heading">
          <span className="eyebrow">{t('pricing.faq.eyebrow')}</span>
          <h2 id="pricing-faq-title">{t('pricing.faq.title')}</h2>
          <p>{t('pricing.faq.text')}</p>
        </div>
        <FaqAccordion items={faqItems} />
      </section>
    </main>
  )
}
